import React, { useEffect, useState } from "react";
import { TbTemplate } from "react-icons/tb";
import { InputGroup, Form } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import lodash from "lodash";
import { toast } from "react-toastify";
import SpecificAPI from "../../../services/SpecificApi";
import localization from "../../../services/localization";
import DeleteModal from "../../Components/DeleteModal";
import ManagementHeader from "../../Components/ManagementHeader";
import CustomSelect from "views/Components/CustomSelect";
import AddEditPromptTemplate from "./AddEditPromptTemplate";
import { PromptTemplateCard } from "views/Components/PromptTemplateCard";
import ViewPromptTemplateModal from "./ViewPromptTemplateModal";
import { getTemplateTypes } from "../../../services/constants";
import { FaSearch } from "react-icons/fa";
import { useLanguageStore } from "context/useLanguageStore";

const emptyPromptTemplate = {
  name: "",
  description: "",
  system_prompt: "",
  user_prompt: "",
  type: "chat",
  rag: false,
  html_format: false,
  system_props: {
    viz_props: [],
    can_user_chat: true,
    chat_visible_to_members: false,
    can_user_select_information_source: false,
    prompt_cost: 0,
  },
  user_prompt: null,
  response_is_editable: false,
  inputs: {
    instruction_variables: [],
    model_parameters: {
      model: "gemini-2.5-flash",
      max_output_tokens: 2048,
      temperature: 0.5,
    },
  },
  resource_ids_to_rag: [],
};
const PromptTemplateManagment = () => {
  const { currentLang } = useLanguageStore();

  const [showAddEditItem, setShowAddEditItem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState({
    value: "all",
    label: localization.all || "All",
  });
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(emptyPromptTemplate);
  const [itemName, setItemName] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [errors, setErrors] = useState({});
  // const [currentPage, setCurrentPage] = useState(1);
  // const [loadingMore, setLoadingMore] = useState(false);
  // const [hasMore, setHasMore] = useState(true);
  const TEMPLATE_TYPES = getTemplateTypes();
  const TEMPLATE_TYPE_FILTER_OPTIONS = [
    { value: "all", label: localization.all || "All" },
    ...TEMPLATE_TYPES,
  ];

  useEffect(() => {
    filterPromptTemplates();
  }, [searchTerm, selectedType, promptTemplates]);

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = async (page = 1, perPage = 10) => {
    // if (page === 1)
    setLoading(true);
    // else setLoadingMore(true);

    try {
      const response = await SpecificAPI.getAllPromptTemplates({});
      const data = response || [];

      data.sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );

      // if (page === 1)
      setPromptTemplates(data);
      // else setPromptTemplates((prev) => [...prev, ...data]);

      // setCurrentPage(response.current_page);
      // setHasMore(!!response.next_page_url);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
      // setLoadingMore(false);
    }
  };

  // useEffect(() => {
  //   const handleScroll = () => {
  //     if (!hasMore || loadingMore) return;

  //     const scrollPosition = window.innerHeight + window.scrollY;
  //     const threshold = document.body.offsetHeight - 300; // 300px from bottom

  //     if (scrollPosition >= threshold) {
  //       getAllData(currentPage + 1);
  //     }
  //   };

  //   window.addEventListener("scroll", handleScroll);
  //   return () => window.removeEventListener("scroll", handleScroll);
  // }, [currentPage, hasMore, loadingMore]);

  const validateForm = (form) => {
    const e = {};

    if (!form.name || !form.name.trim()) {
      e.name = localization.name_required || "Name is required";
    }
    if (!form.type || !form.type.trim()) {
      e.type = localization.template_type_required || "Select a template type";
    }

    return e;
  };

  const handleSubmitItem = async (data) => {
    const validationErrors = validateForm(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      setLoading(true);
      if (editingItem?.id) {
        const res = await SpecificAPI.updatePromptTemplate({
          ...data,
          _id: editingItem.id,
        });
        setPromptTemplates((prev) =>
          prev.map((ev) => (ev.id === editingItem.id ? res.data : ev)),
        );
      } else {
        const created = await SpecificAPI.createPromptTemplate(data);
        setPromptTemplates((prev) => [...prev, created.data]);
      }
      setShowAddEditItem(false);
      setEditingItem(emptyPromptTemplate);

      toast.success(
        localization.prompt_template_toast_saved ||
          "Prompt template saved successfully!",
      );
    } catch (err) {
      console.error("Error saving item:", err);
      toast.error(
        err?.response?.data?.error ||
          localization.prompt_template_toast_failed ||
          "Failed to save prompt template!",
      );
    } finally {
      setLoading(false);
    }
  };

  const filterPromptTemplates = () => {
    let filtered = promptTemplates;
    if (searchTerm) {
      filtered = filtered.filter((cls) =>
        cls.name?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (selectedType.value !== "all") {
      filtered = filtered.filter(
        (cls) => cls.type?.toLowerCase() === selectedType.value.toLowerCase(),
      );
    }

    setFilteredItems(filtered);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowAddEditItem(true);
    setErrors({});
  };

  const handleEditFromView = (item) => {
    setEditingItem(item);
    setShowViewModal(false);
    setShowAddEditItem(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setItemName(item.title || "");
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.deletePromptTemplate(itemToDelete.id);

      setPromptTemplates((prev) =>
        prev.filter((ev) => ev.id !== itemToDelete.id),
      );

      toast.success("Prompt template deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete prompt template. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddEditItem(false);
    setEditingItem(emptyPromptTemplate);
    setErrors({});
  };

  const handleView = (item) => {
    setViewingItem(item);
    setShowViewModal(true);
  };

  const handleFormChange = (name, value) => {
    setEditingItem((prev) => {
      if (!prev) return { ...emptyPromptTemplate, [name]: value };
      return { ...prev, [name]: value };
    });

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleNestedChange = (path, value) => {
    setEditingItem((prev) => {
      const updated = { ...prev };
      lodash.set(updated, path, value);
      return updated;
    });
  };

  return (
    <div className="d-flex flex-column mb-5 w-100 p-8 main-page">
      <div
        className="d-flex flex-column align-items-start mb-5 w-100"
        style={{ paddingRight: "2rem" }}
      >
        <ManagementHeader
          title={localization.all_prompt_templates || "All Prompt Templates"}
          subtitle={localization.manage_templates || "Manage prompt templates"}
          // icon={TbTemplate}
          buttonText={localization.add_prompt_template || "Add prompt template"}
          onButtonClick={() => {
            setShowAddEditItem(true);
            setEditingItem(emptyPromptTemplate);
            setErrors({});
          }}
          showBack={true}
          backTo="/schools"
          backLabel="Back to organization"
        />

        {/* Filters Row */}
        <div className="d-flex align-items-center gap-3 w-100 mt-4">
          {/* Search Input with Icon */}
          <InputGroup className="search-field flex-grow-1">
            <InputGroup.Text id="search-addon" className="customSearchAddon">
              <FaSearch className="icon-btn" />{" "}
            </InputGroup.Text>
            <Form.Control
              placeholder={
                localization.search_by_prompt_name || "Search by prompt name..."
              }
              aria-label="Search"
              aria-describedby="search-addon"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="customSearchInput"
            />
          </InputGroup>

          {/* Grade Select */}
          <div style={{ flex: "0 0 250px" }}>
            <CustomSelect
              options={TEMPLATE_TYPE_FILTER_OPTIONS}
              value={selectedType}
              onChange={(opt) => setSelectedType(opt)}
              placeholder={localization.select_type || "Select a type"}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <ClipLoader size={40} color="var(--primary-icon)" />
        </div>
      ) : (
        <div className="row g-4 w-100">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div key={item.id} className="col-6 col-md-6 col-lg-4 d-flex">
                <PromptTemplateCard
                  item={item}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onView={handleView}
                />
              </div>
            ))
          ) : (
            <p className="text-center text-muted">
              {localization.no_prompt_templates || "No prompt templates found."}
            </p>
          )}
        </div>
      )}
      {/* {loadingMore && (
          <div className="flex justify-center items-center py-4">
            <ClipLoader size={30} color="#ff9800" />
          </div>
        )} */}

      <AddEditPromptTemplate
        show={showAddEditItem}
        title={
          editingItem?.id
            ? localization.edit_prompt_template || "Edit Prompt template"
            : localization.add_prompt_template_title || "Add Prompt template"
        }
        formData={{
          ...emptyPromptTemplate,
          ...editingItem,
          system_props: {
            viz_props: editingItem?.system_props?.viz_props || [],
            can_user_chat: editingItem?.system_props?.can_user_chat || false,
            chat_visible_to_members:
              editingItem?.system_props?.chat_visible_to_members || false,
            can_user_select_information_source:
              editingItem?.system_props?.can_user_select_information_source ||
              false,
            prompt_cost: editingItem?.system_props?.prompt_cost || 0,
          },
        }}
        errors={errors}
        onChange={handleFormChange}
        onChangeNested={handleNestedChange}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitItem(editingItem);
        }}
        onClose={handleCloseModal}
        loading={loading}
      />

      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />

      <ViewPromptTemplateModal
        show={showViewModal}
        item={viewingItem}
        onEdit={handleEditFromView}
        onClose={() => setShowViewModal(false)}
      />
    </div>
  );
};

export default PromptTemplateManagment;
